import {
  Box,
  Card,
  Checkbox,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { SLPFormStep } from "utils/enums/slp-form";
import { showLoader } from "utils/helper";
import {
  IHopesGoalsAndAmbitions,
  IWhereToFromHere,
  IWhereToFromHereFormValues,
} from "utils/interfaces/slp-form";
import { useForm, Controller } from "react-hook-form";
import { useLocation } from "react-router-dom";
import {
  HopesGoalsAndAmbitionsPayload,
  HopesGoalsAndAmbitionsSuccessPayload,
} from "store/slpForm/types";
import { ISwitchCourse } from "utils/interfaces/student-enrolment";
import {
  GetCurrentCoursesForAddEnrolmentPayload,
  GetCurrentCoursesForAddEnrolmentSuccessPayload,
} from "store/student/types";
import {
  DATE_ONLY_FORMAT,
  DATE_PICKER_FORMAT,
  FACE_TO_FACE,
  INPUT_VALIDATION_NO_WHITE_SPACE_WITH_SPECIAL_CHARACTER_REGEX,
} from "utils/constants/constant";
import { ICurrentCourseList } from "utils/interfaces/student";
import CheckedBoxIcon from "components/CheckedBoxIcon";
import RadioCheckBoxIcon from "components/RadioCheckBoxIcon";
import RadioCheckedBoxIcon from "components/RadioCheckedBoxIcon";
import projectTheme from "app.theme";
import dayjs from "dayjs";
import {
  nextSLPDueDateError,
  slpAnswerWhiteSpaceError,
  sLPCompletedOnDateError,
} from "utils/helper/validation-message-helper";
import CheckboxIcon from "components/CheckBoxIcon";
import CommonDatePicker from "components/CommonDatePicker";
import SLPFormButtons from "../SLPFormButtons";

interface IWhereToFromHereProps {
  hopesGoalsAndAmbitionsRequest: (payload: HopesGoalsAndAmbitionsPayload) => {};
  getCurrentCoursesForAddEnrolmentRequest: (
    payload: GetCurrentCoursesForAddEnrolmentPayload
  ) => {};
  handleNext: (data?: IWhereToFromHere) => void;
}

const WhereToFromHere: React.FC<IWhereToFromHereProps> = ({
  hopesGoalsAndAmbitionsRequest,
  getCurrentCoursesForAddEnrolmentRequest,
  handleNext,
}) => {
  const location = useLocation<{
    slpId: number;
    partNumber: SLPFormStep;
    studentId: number;
    isCompletedSLP: boolean;
  }>();
  const { slpId, partNumber, isCompletedSLP } = location.state;
  const [hopesGoalsAndAmbitionsData, setHopesGoalsAndAmbitionsData] =
    useState<IHopesGoalsAndAmbitions>();
  const [currentCourses, setCurrentCourses] = useState<ICurrentCourseList[]>();

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<IWhereToFromHereFormValues>({
    defaultValues: {
      answers: {},
      courseIds: [],
      courseNames: [],
      preferredCourseDelivery: [],
      dateCompleted: dayjs(),
      nextSLPDueDate: dayjs().add(3, "month"),
    },
  });
  const courseIds = watch("courseIds");
  const preferredCourseDelivery = watch("preferredCourseDelivery");

  const onHopesGoalsAndAmbitionsDataSuccess = useCallback(
    (response: HopesGoalsAndAmbitionsSuccessPayload) => {
      const data = response.hopesGoalsAndAmbitionsData;
      setHopesGoalsAndAmbitionsData(data);
      const initialAnswers = Object.fromEntries(
        data.questionsAnswers.map((qa) => [qa.id, qa.answer || ""])
      );

      setValue("answers", initialAnswers);
      if (data?.dateCompleted)
        setValue("dateCompleted", dayjs(data.dateCompleted));
      if (data?.nextSLPDueDate)
        setValue("nextSLPDueDate", dayjs(data.nextSLPDueDate));
    },
    [setValue]
  );

  const getHopesGoalsAndAmbitionsData = useCallback(() => {
    if (!hopesGoalsAndAmbitionsRequest) return;

    showLoader();
    hopesGoalsAndAmbitionsRequest({
      values: {
        slpId,
        partNumber,
      },
      callback: onHopesGoalsAndAmbitionsDataSuccess,
    });
  }, [
    hopesGoalsAndAmbitionsRequest,
    onHopesGoalsAndAmbitionsDataSuccess,
    slpId,
    partNumber,
  ]);

  const onGetCurrentCoursesSuccess = (
    response: GetCurrentCoursesForAddEnrolmentSuccessPayload
  ) => {
    setCurrentCourses(response.currentCourses);
    const switchCourses: ISwitchCourse[] = response.currentCourses.map(
      (course) => ({
        courseOfferingId: course.id,
        preferredCourseDelivery: course.isBlended ? FACE_TO_FACE : null,
        courseName: course.courseName,
      })
    );
    setValue("preferredCourseDelivery", switchCourses);
  };

  const getCurrentCourses = () => {
    if (getCurrentCoursesForAddEnrolmentRequest) {
      showLoader();

      const payload = {
        values: { id: location?.state?.studentId },
        callback: onGetCurrentCoursesSuccess,
      };

      getCurrentCoursesForAddEnrolmentRequest(payload);
    }
  };

  const handleSelectedCourses = (e: React.ChangeEvent<HTMLInputElement>) => {
    const currentIndex = courseIds.indexOf(Number(e.target.value));
    const newCourseIds = [...courseIds];

    if (currentIndex === -1) {
      newCourseIds.push(Number(e.target.value));
    } else {
      newCourseIds.splice(currentIndex, 1);
    }

    const courseNames = currentCourses
      ?.filter((course) => newCourseIds.includes(course.id))
      .map((course) => course.courseName) as string[];

    setValue("courseNames", courseNames);
    setValue("courseIds", newCourseIds);
    setValue("preferredCourseDelivery", preferredCourseDelivery);
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    courseOfferingId: number,
    courseName: string
  ) => {
    const courseIndex = preferredCourseDelivery.findIndex(
      (x) => x.courseOfferingId === courseOfferingId
    );
    if (courseIndex === -1) {
      preferredCourseDelivery.push({
        courseOfferingId,
        preferredCourseDelivery: event.currentTarget.value,
        courseName,
      });
    } else {
      preferredCourseDelivery[courseIndex].preferredCourseDelivery =
        event.currentTarget.value;
    }
    setValue("preferredCourseDelivery", preferredCourseDelivery);
  };

  useEffect(() => {
    getHopesGoalsAndAmbitionsData();
    getCurrentCourses();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getHopesGoalsAndAmbitionsData]);

  const onClickSubmit = (data: IWhereToFromHereFormValues) => {
    const payload: IWhereToFromHere = {
      slpId,
      partNumber,
      questionAnswers: Object.entries(data.answers).map(([id, answer]) => ({
        questionId: Number(id),
        answer,
      })),
      studentId: location?.state?.studentId,
      preferredCourseDelivery: preferredCourseDelivery
        .filter((course) => courseIds.includes(course.courseOfferingId))
        .map((course) => {
          return {
            courseOfferingId: course.courseOfferingId,
            preferredCourseDelivery: course.preferredCourseDelivery,
          };
        }),
      dateCompleted: dayjs(data.dateCompleted),
      nextSLPDueDate: dayjs(data.nextSLPDueDate).format(DATE_ONLY_FORMAT),
    };

    handleNext(payload);
  };

  return (
    <form onSubmit={handleSubmit(onClickSubmit)}>
      <Grid container columnSpacing={{ xs: 2, lg: 3, xl: "24px" }}>
        {hopesGoalsAndAmbitionsData?.questionsAnswers.map((qa) => (
          <Grid item xs={12} key={qa.questionOrder}>
            <Typography variant="body2" className="gray-text-sm" sx={{ pb: 1 }}>
              {`${qa.questionOrder}) ${qa.question}`}
            </Typography>
            <Controller
              name={`answers.${qa.id}`}
              control={control}
              rules={{
                pattern:
                  INPUT_VALIDATION_NO_WHITE_SPACE_WITH_SPECIAL_CHARACTER_REGEX,
              }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  multiline
                  fullWidth
                  rows={3}
                  error={!!error}
                  helperText={slpAnswerWhiteSpaceError(error)}
                  className="suggestion-textarea"
                />
              )}
            />
          </Grid>
        ))}
        <Grid item xs={12} md={6}>
          <Box sx={{ mb: 3 }}>
            <CommonDatePicker<IWhereToFromHereFormValues>
              format={DATE_PICKER_FORMAT}
              name="dateCompleted"
              control={control}
              disabled={isCompletedSLP}
              disableFuture
              rules={{
                required: true,
                validate: (value: string | dayjs.Dayjs | null) => {
                  if (isCompletedSLP) return true;
                  const selectedDate = dayjs(value);
                  const maxDate = dayjs();

                  return selectedDate.isBefore(maxDate);
                },
              }}
              label="Date Completed"
              value={watch("dateCompleted") ?? dayjs()}
              helperText={sLPCompletedOnDateError(errors.dateCompleted)}
              error={!!errors.dateCompleted}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ mb: 3 }}>
            <CommonDatePicker<IWhereToFromHereFormValues>
              name="nextSLPDueDate"
              control={control}
              disabled={isCompletedSLP}
              disablePast
              rules={{
                required: true,
                validate: (value: string | dayjs.Dayjs | null) => {
                  if (isCompletedSLP) return true;
                  const selectedDate = dayjs(value);
                  const minDate = dayjs().hour(0).minute(0).second(0);

                  return selectedDate.isAfter(minDate);
                },
              }}
              label="Next SLP Due Date"
              value={watch("nextSLPDueDate") ?? dayjs().add(3, "month")}
              format={DATE_PICKER_FORMAT}
              helperText={nextSLPDueDateError(errors.nextSLPDueDate)}
              error={!!errors.nextSLPDueDate}
            />
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Card className="p-0">
            <Box className="card-inner-head mb-24">
              <Typography variant="h3" sx={{ fontWeight: 500 }}>
                Course Preferences
              </Typography>
            </Box>
            <Box>
              {currentCourses && !!currentCourses.length ? (
                <>
                  <Box className="stdenrol-course">
                    <Typography sx={{ paddingBottom: "7px" }} variant="h5">
                      Current and Ongoing Courses Offerings
                    </Typography>
                    <TableContainer
                      className="sticky-container"
                      sx={{ maxHeight: "350px" }}
                    >
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                [projectTheme.breakpoints.down("lg")]: {
                                  minWidth: "442px",
                                },
                                [projectTheme.breakpoints.down("sm")]: {
                                  minWidth: "0",
                                },
                              }}
                            >
                              Course
                            </TableCell>
                            <TableCell
                              className="course-del"
                              align="center"
                              sx={{
                                width: "260px",
                                minWidth: "260px",
                                maxWidth: "260px",
                              }}
                            >
                              Preferred Course Delivery
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {currentCourses?.map((course) => (
                            <TableRow>
                              <TableCell scope="row">
                                <Controller
                                  control={control}
                                  {...register("courseIds")}
                                  render={() => (
                                    <>
                                      <FormControlLabel
                                        value={course?.id}
                                        className="add-new-enrlmnt"
                                        control={
                                          <Checkbox
                                            checked={
                                              courseIds?.indexOf(course.id) > -1
                                            }
                                            icon={<CheckboxIcon />}
                                            checkedIcon={<CheckedBoxIcon />}
                                            onChange={(e) =>
                                              handleSelectedCourses(e)
                                            }
                                            className="stepper-check-course"
                                          />
                                        }
                                        label={course.courseName}
                                        labelPlacement="end"
                                      />
                                    </>
                                  )}
                                />
                              </TableCell>
                              <TableCell>
                                {course.isBlended ? (
                                  <Controller
                                    control={control}
                                    {...register("preferredCourseDelivery")}
                                    render={({ field }) => (
                                      <RadioGroup
                                        row
                                        {...field}
                                        onChange={(e) =>
                                          courseIds?.indexOf(course.id) > -1
                                            ? handleChange(
                                                e,
                                                course.id,
                                                course.courseName
                                              )
                                            : null
                                        }
                                      >
                                        <FormControlLabel
                                          checked={
                                            courseIds?.indexOf(course.id) >
                                              -1 &&
                                            watch(
                                              "preferredCourseDelivery"
                                            )?.find(
                                              (x) =>
                                                x.courseOfferingId === course.id
                                            )?.preferredCourseDelivery ===
                                              "Face To Face"
                                          }
                                          value="Face To Face"
                                          control={
                                            <Radio
                                              icon={<RadioCheckBoxIcon />}
                                              checkedIcon={
                                                <RadioCheckedBoxIcon />
                                              }
                                            />
                                          }
                                          label="Face To Face"
                                        />
                                        <FormControlLabel
                                          checked={
                                            courseIds?.indexOf(course.id) >
                                              -1 &&
                                            watch(
                                              "preferredCourseDelivery"
                                            )?.find(
                                              (x) =>
                                                x.courseOfferingId === course.id
                                            )?.preferredCourseDelivery ===
                                              "Online"
                                          }
                                          value="Online"
                                          control={
                                            <Radio
                                              sx={{ marginLeft: "15px" }}
                                              icon={<RadioCheckBoxIcon />}
                                              checkedIcon={
                                                <RadioCheckedBoxIcon />
                                              }
                                            />
                                          }
                                          label="Online"
                                        />
                                      </RadioGroup>
                                    )}
                                  />
                                ) : (
                                  ""
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </>
              ) : (
                <Typography className="no-record-text">
                  Currently no courses available
                </Typography>
              )}
            </Box>
          </Card>
        </Grid>
      </Grid>
      <SLPFormButtons activePartNumber={partNumber} />
    </form>
  );
};

export default WhereToFromHere;
